'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
  marketingEmails: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: Props) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: { marketingEmails: true },
  });

  const passwordValue = watch('password');

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    // BACKEND: POST /api/auth/register with { firstName, lastName, email, password, marketingEmails }
    await new Promise((r) => setTimeout(r, 1400));
    setIsLoading(false);
    setSuccess(true);
    setTimeout(() => router.push('/'), 2000);
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center fade-in">
        <div className="w-16 h-16 bg-positive-bg rounded-full flex items-center justify-center mb-4">
          <Icon name="CheckCircleIcon" size={36} className="text-positive" />
        </div>
        <h2 className="text-xl font-700 text-foreground mb-2">Account created!</h2>
        <p className="text-sm text-muted-foreground">
          Welcome to TravelHub. Redirecting you to the homepage...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      {/* Name Row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5" htmlFor="signup-first">
            First name
          </label>
          <input
            id="signup-first"
            type="text"
            autoComplete="given-name"
            placeholder="Alex"
            className={`input-base w-full px-4 py-3 text-sm ${errors.firstName ? 'border-negative' : ''}`}
            {...register('firstName', { required: 'Required' })}
          />
          {errors.firstName && (
            <p className="text-xs text-negative mt-1">{errors.firstName.message}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5" htmlFor="signup-last">
            Last name
          </label>
          <input
            id="signup-last"
            type="text"
            autoComplete="family-name"
            placeholder="Rivera"
            className={`input-base w-full px-4 py-3 text-sm ${errors.lastName ? 'border-negative' : ''}`}
            {...register('lastName', { required: 'Required' })}
          />
          {errors.lastName && (
            <p className="text-xs text-negative mt-1">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5" htmlFor="signup-email">
          Email address
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="alex@example.com"
          className={`input-base w-full px-4 py-3 text-sm ${errors.email ? 'border-negative' : ''}`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs text-negative mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5" htmlFor="signup-password">
          Password
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">
          Minimum 8 characters, at least one number
        </p>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Create a strong password"
            className={`input-base w-full px-4 py-3 pr-11 text-sm ${errors.password ? 'border-negative' : ''}`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Must be at least 8 characters' },
              pattern: { value: /(?=.*\d)/, message: 'Must contain at least one number' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-negative mt-1">{errors.password.message}</p>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5" htmlFor="signup-confirm">
          Confirm password
        </label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Repeat your password"
            className={`input-base w-full px-4 py-3 pr-11 text-sm ${errors.confirmPassword ? 'border-negative' : ''}`}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === passwordValue || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
          >
            <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="text-xs text-negative mt-1">{errors.confirmPassword.message}</p>
        )}
      </div>

      {/* Terms */}
      <div>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox-custom mt-0.5 flex-shrink-0"
            {...register('agreeToTerms', { required: 'You must agree to the terms to continue' })}
          />
          <span className="text-sm text-foreground leading-snug">
            I agree to TravelHub's{' '}
            <a href="/" className="text-primary hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/" className="text-primary hover:underline">Privacy Policy</a>
          </span>
        </label>
        {errors.agreeToTerms && (
          <p className="text-xs text-negative mt-1">{errors.agreeToTerms.message}</p>
        )}
      </div>

      {/* Marketing */}
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          className="checkbox-custom mt-0.5 flex-shrink-0"
          {...register('marketingEmails')}
        />
        <span className="text-sm text-muted-foreground leading-snug">
          Send me price drop alerts and exclusive travel deals by email
        </span>
      </label>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-70"
        style={{ minHeight: '48px' }}
      >
        {isLoading ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
            Creating your account...
          </>
        ) : (
          <>
            <Icon name="SparklesIcon" size={16} />
            Create Free Account
          </>
        )}
      </button>

      {/* Switch to login */}
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary font-semibold hover:underline"
        >
          Sign in
        </button>
      </p>
    </form>
  );
}